import {
  createUserWithEmailAndPassword,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  User,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { auth, db } from "./firebaseConfig";
import defaultpfp from "../assets/defaultpfp.jpg";
import { UserData } from "../data/data";

export let defaultProfilePhotoUrl = defaultpfp;

export const signUp = async (
  email: string,
  password: string,
  phoneNumber: string,
  userName: string
) => {
  // auth.beforeAuthStateChanged
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    const user = userCredential.user;

    if (user) {
      const userDocRef = doc(db, "users", user.uid);
      const userData: UserData = {
        userId: user.uid,
        email: user.email || email,
        username: userName,
        profilePhotoUrl: defaultProfilePhotoUrl,
        verified: false,
        phoneNumber: phoneNumber,
        profileComplete: false,
      };
      // check if role is guest then throw error you need to select a role

      await setDoc(userDocRef, userData);

      return user;
    } else {
      throw new Error("User not found");
    }
  } catch (error: any) {
    switch (error.code) {
      case "auth/email-already-in-use":
        throw new Error("Email address already in use");
      case "auth/invalid-email":
        throw new Error("Invalid email address");
      case "auth/weak-password":
        throw new Error("Password is too weak");
      default:
        throw new Error("Signup failed");
    }
  }
};

export const signIn = async (
  email: string,
  password: string,
  rememberMe?: boolean
) => {
  const persistenceType = rememberMe
    ? browserLocalPersistence
    : browserSessionPersistence;

  try {
    await setPersistence(auth, persistenceType);
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    // get current user
    const user = userCredential.user;
    // check if user has user.id else set it
    if (user) {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          userId: user.uid,
        });
      }
    }
    return userCredential.user;
  } catch (error: any) {
    console.log(error.code);
    switch (error.code) {
      case "auth/invalid-email":
        throw new Error("Invalid email address");
      case "auth/user-disabled":
        throw new Error("User account has been disabled");
      case "auth/user-not-found":
      case "auth/wrong-password":
        throw new Error("Incorrect email or password");
      case "auth/invalid-credential":
        throw new Error("Invalid credential");
      default:
        throw new Error("Login failed");
    }
  }
};

export const getUserData = async (): Promise<UserData> => {
  const user = auth.currentUser;

  if (user) {
    const userDocRef = doc(db, "users", user.uid);
    try {
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }
      const userData = userDoc.data() as UserData;

      // Check if the userId field is present, if not, update the document
      if (userData && !userData.userId) {
        await updateDoc(userDocRef, {
          userId: user.uid,
        });
        // Add the userId to the userData object that will be returned
        userData.userId = user.uid;
      }

      return userData;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  } else {
    throw new Error("No user is currently logged in");
  }
};

export const ensureUserFields = async (
  user: User,
  additionalData = {
    username: "",
    role: "visitor",
    profileComplete: false,
    phoneNumber: "",
    email: "",
  }
) => {
  const userRef = doc(db, "users", user.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    const newUserDoc = {
      userId: user.uid,
      email: user.email || additionalData.email,
      username: user.displayName || user.email?.split("@")[0] || "User",
      profilePhotoUrl: user.photoURL || defaultProfilePhotoUrl,
      verified: user.emailVerified,
      phoneNumber: user.phoneNumber || "",
      role: additionalData.role || "visitor",
      profileComplete: additionalData.profileComplete || false,
    };
    await setDoc(userRef, newUserDoc);
  } else {
    // If the document exists, check for missing fields and update them
    const userData = userSnap.data() as UserData;
    const updatedData: Partial<UserData> = {};

    if (userSnap.data().hasOwnProperty("subscribed")) {
      await updateDoc(userRef, {
        subscribed: deleteField(),
      });
    }

    if (!userData.username) {
      updatedData.username =
        additionalData.username || user.email?.split("@")[0];
    }
    if (!userData.role) {
      updatedData.role = additionalData.role || "visitor";
    }
    if (userData.profileComplete === undefined) {
      updatedData.profileComplete = additionalData.profileComplete || false;
    }
    if (!userData.phoneNumber) {
      updatedData.phoneNumber = additionalData.phoneNumber || "";
    }

    if (Object.keys(updatedData).length > 0) {
      await updateDoc(userRef, updatedData);
    }
  }
};

export const forgotPassword = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

export const signInWithGoogle = async () => {
  try {
    // For web platforms, use signInWithPopup
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);

    // Return the Firebase user
    return result.user;
  } catch (error: any) {
    console.error("Google sign in failed:", error);
    throw new Error("Google sign in failed with error: " + error.message);
  }
};

export const signOut = async () => {
  await auth.signOut();
};

const provider = new OAuthProvider("apple.com");

export const signInWithApple = async () => {
  try {
    const result = await signInWithPopup(auth, provider);

    return result.user;
  } catch (error: any) {
    console.error("Apple sign in failed:", error);
    throw new Error("Apple sign in failed with error: " + error.message);
  }
};
